import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { ErrorAlert } from "@/components/ui/ErrorAlert";
import { SettingsSection } from "@/components/SettingsSection";
import { useChangePasswordMutation } from "@/features/account/hooks/useAccount";
import { extractMessage } from "@/lib/errors";

export function PasswordSection() {
  const { t } = useTranslation();
  const changePassword = useChangePasswordMutation();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      await changePassword.mutateAsync({ currentPassword, newPassword });
      setCurrentPassword("");
      setNewPassword("");
    } catch (err) {
      setError(extractMessage(err));
    }
  }

  return (
    <SettingsSection title={t("account.security.title")}>
      <form onSubmit={handleSubmit} className="flex flex-col gap-3 px-4 py-3">
        <div className="flex flex-col gap-3 sm:flex-row">
          <Input
            type="password"
            placeholder={t("account.security.currentPasswordPlaceholder")}
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            autoComplete="current-password"
            required
            className="sm:flex-1"
          />
          <Input
            type="password"
            placeholder={t("account.security.newPasswordPlaceholder")}
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            autoComplete="new-password"
            minLength={8}
            required
            className="sm:flex-1"
          />
        </div>
        {error && <ErrorAlert message={error} />}
        <div>
          <Button type="submit" size="sm" disabled={changePassword.isPending}>
            {changePassword.isPending
              ? t("account.security.updating")
              : t("account.security.submit")}
          </Button>
        </div>
      </form>
    </SettingsSection>
  );
}
